import { useState, useEffect } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import { Brain, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export default function Intelligence() {
  const [reports, setReports] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/intelligence/reports');
      setReports(data);
    } catch (error) {
      toast.error('Failed to load intelligence reports');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setAnalyzing(true);
    try {
      const { data } = await axios.post('/intelligence/analyze', { query });
      toast.success('Intelligence analysis complete');
      setQuery('');
      setReports([data, ...reports]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="intelligence-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase">Intelligence Analysis</h1>
        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
          AI-powered tactical intelligence
        </p>
      </div>

      <div className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 corner-accent">
        <form onSubmit={handleAnalyze} className="space-y-4" data-testid="intelligence-form">
          <div>
            <label className="text-xs font-mono text-zinc-500 uppercase block mb-2">
              Intelligence Query
            </label>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter intelligence data or strategic question for AI analysis..."
              rows={4}
              className="bg-zinc-900/50 border-zinc-800 rounded-none font-mono text-sm resize-none"
              disabled={analyzing}
              data-testid="intelligence-query-input"
            />
          </div>
          <Button
            type="submit"
            disabled={analyzing || !query.trim()}
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase tracking-wider font-bold text-xs border border-sky-400/20 hover:border-sky-400"
            data-testid="analyze-button"
          >
            {analyzing ? (
              <>
                <Brain size={16} className="mr-2 animate-pulse" />
                ANALYZING...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Analyze Intelligence
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight uppercase">Intelligence Reports</h2>
        {loading ? (
          <div className="text-center py-8 text-sky-500 font-mono text-sm">
            LOADING REPORTS...
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-8 text-zinc-600 font-mono text-sm">
            No intelligence reports yet
          </div>
        ) : (
          reports.map((report) => (
            <div
              key={report.id}
              className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6 hover:border-zinc-700 transition-colors"
              data-testid={`report-${report.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-sky-500/10 border border-sky-500/30 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Brain size={20} className="text-sky-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-sm font-bold uppercase tracking-tight text-sky-500">
                      Query
                    </h3>
                    <span className="text-xs font-mono text-zinc-600">
                      {new Date(report.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-300 mb-4 bg-zinc-900/50 p-3 rounded-sm border border-zinc-800 font-mono">
                    {report.query}
                  </p>
                  <h3 className="text-sm font-bold uppercase tracking-tight text-emerald-500 mb-2">
                    Analysis
                  </h3>
                  <div className="text-sm text-zinc-400 bg-zinc-900/50 p-4 rounded-sm border border-zinc-800 whitespace-pre-wrap">
                    {report.analysis}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}